import { useState } from 'react';

import { api } from '../lib/api';
import { Card } from './ui';

export interface ServerIdentity {
  id: string;
  name: string;
  hostname: string;
  ipAddress: string | null;
  location: string | null;
  machineHostname: string | null;
  machineFqdn: string | null;
  notes: string | null;
}

/**
 * Name, location and the machine's own identity.
 *
 * Three different things that a single "hostname" column would blur:
 *
 *  - **name** is what a person calls it, and a poll must never overwrite it.
 *  - **hostname / IP** is how PlatformIQ reaches it, and is the unique key.
 *  - **machineHostname** is what the box calls itself. On this estate those are
 *    VM UUIDs, which is precisely why it cannot double as the name.
 *
 * Location is typed, because nothing on the host reliably knows. The FQDN is
 * shown beside it since it occasionally gives the answer away — one of these
 * hosts resolves under `mtl2.ca.leaseweb.net`, which is Montreal.
 */
export function IdentityCard({
  server,
  canEdit,
  onSaved,
}: {
  server: ServerIdentity;
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(server.name);
  const [location, setLocation] = useState(server.location ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/servers/${server.id}/details`, {
        method: 'PATCH',
        body: JSON.stringify({ name: name.trim(), location: location.trim() || null }),
      });
      setEditing(false);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Identity"
      actions={
        canEdit && !editing ? (
          <button type="button" className="ghost" onClick={() => setEditing(true)}>
            Edit
          </button>
        ) : undefined
      }
    >
      {editing ? (
        <>
          <div className="cost-form">
            <label className="wide">
              Name
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. ABC demo host" />
            </label>
            <label className="wide">
              Location
              <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
                placeholder="e.g. Leaseweb, Montreal, Canada" />
            </label>
          </div>
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="cost-actions">
            <button type="button" disabled={busy || !name.trim()} onClick={() => void save()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="ghost" disabled={busy}
              onClick={() => { setEditing(false); setName(server.name); setLocation(server.location ?? ''); }}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <dl className="facts">
          <dt>Name</dt>
          <dd>{server.name}</dd>
          <dt>Location</dt>
          <dd>
            {server.location ?? (
              <span className="muted">
                not recorded
                {/* The FQDN is the only place in the whole probe a location hint
                    exists, so it is offered rather than left for someone to
                    notice on a server page they may never open. */}
                {server.machineFqdn?.includes('.') && (
                  <> — the FQDN suggests <code>{server.machineFqdn.split('.').slice(1).join('.')}</code></>
                )}
              </span>
            )}
          </dd>
          <dt>Address</dt>
          <dd className="mono">{server.ipAddress ?? server.hostname}</dd>
          <dt>Machine name</dt>
          <dd className="mono small">
            {server.machineHostname ?? <span className="muted">—</span>}
          </dd>
          {server.machineFqdn && server.machineFqdn !== server.machineHostname && (
            <>
              <dt>FQDN</dt>
              <dd className="mono small">{server.machineFqdn}</dd>
            </>
          )}
          {server.notes && (
            <>
              <dt>Notes</dt>
              <dd>{server.notes}</dd>
            </>
          )}
        </dl>
      )}
    </Card>
  );
}
